import React, { DragEvent, TouchEvent, useCallback, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { Control, FieldValues, useFieldArray } from 'react-hook-form'
import { useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { AddImageButton } from '@/components/molecules/add-image-button'
import { UploadFileImage } from '@/components/molecules/upload-file-image'

export interface FormType extends FieldValues {
  images: Array<{ path: string; uploadThumbnail?: string }>
}
type Props<TFormType extends FormType> = {
  control: TFormType extends FormType ? Control<TFormType, any> : never
  rules?: any
}

export const InputMultipleImage = <T extends FormType>({ control, rules }: Props<T>) => {
  const imageInput = useFieldArray({ control, name: 'images' })
  const [dragoverItemId, setDragoverItemId] = useState<string>()
  const [draggingItemId, setDraggingItemId] = useState<string>()
  const [isDragToAfter, setIsDragToAfter] = useState<boolean>()
  const changeDisplayOrder = (newList: { path: string; id: string; uploadThumbnail?: string }[]) => {
    imageInput.replace(newList)
  }
  const { dragStart, dragOver, dragDrop, isDropToAfter, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom({
    list: imageInput.fields,
    onChange: changeDisplayOrder,
    direction: 'horizontal',
  })
  const getTouchDropTarget = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      const touch = event.touches[0] ?? event.changedTouches[0]
      if (!touch) return null
      const element = document.elementFromPoint(touch.clientX, touch.clientY)
      const tileElement = element?.closest('[data-draggable-image-id]') as HTMLElement | null
      const targetId = tileElement?.dataset.draggableImageId
      if (!targetId) return null
      return imageInput.fields.some(field => field.id === targetId) ? { id: targetId, clientX: touch.clientX, target: tileElement } : null
    },
    [imageInput.fields],
  )
  const handleTouchStartWithVisual = (pathId: string) => (event: TouchEvent<HTMLElement>) => {
    setDraggingItemId(pathId)
    handleTouchStart(event)
  }
  const handleTouchMoveWithVisual = (event: TouchEvent<HTMLElement>) => {
    const dropTarget = getTouchDropTarget(event)
    if (dropTarget) {
      setDragoverItemId(dropTarget.id)
      const rect = dropTarget.target.getBoundingClientRect()
      setIsDragToAfter(Math.abs(rect.left - dropTarget.clientX) > Math.abs(rect.right - dropTarget.clientX))
    } else {
      setDragoverItemId('')
    }
    handleTouchMove(event)
  }
  const handleTouchEndWithVisual = (event: TouchEvent<HTMLElement>) => {
    handleTouchEnd(event)
    setDraggingItemId('')
    setDragoverItemId('')
  }

  const appendDragGhostImageDom = (imagePath: string, e: DragEvent<HTMLDivElement>) => {
    const img = new Image(e.currentTarget.clientWidth, e.currentTarget.clientHeight)
    img.src = imagePath || require('@/static/images/dummy-120x80.png')
    img.id = 'drag-ghost-image'
    img.setAttribute('style', 'position: absolute; top: -1000px; border-radius: 5px')
    document.body.appendChild(img)
    e.dataTransfer.setDragImage(
      img,
      e.clientX - e.currentTarget.getBoundingClientRect().x,
      e.clientY - e.currentTarget.getBoundingClientRect().y,
    )
  }
  const removeDragGhostImageDom = () => {
    const ghostImage = document.getElementById('drag-ghost-image')
    ghostImage?.parentNode?.removeChild(ghostImage)
  }

  // blob URL メモリリーク対策：アンマウント時に全解放（最新の fields を参照する）
  const imageFieldsRef = React.useRef(imageInput.fields)
  useEffect(() => {
    imageFieldsRef.current = imageInput.fields
  }, [imageInput.fields])

  useEffect(() => {
    return () => {
      imageFieldsRef.current.forEach(field => {
        if (field.uploadThumbnail?.startsWith('blob:')) {
          URL.revokeObjectURL(field.uploadThumbnail)
        }
      })
    }
  }, [])

  return (
    <div css={inputImageStyle}>
      <div className="image-tile" style={{ paddingBottom: rules ? 24 : 0 }}>
        {imageInput.fields.map((uploadImg, index) => (
          <div key={uploadImg.id} style={{ position: 'relative' }} data-draggable-image-id={uploadImg.id}>
            <UploadFileImage
              value={uploadImg.uploadThumbnail || uploadImg.path}
              draggable="true"
              onDragStart={e => {
                appendDragGhostImageDom(uploadImg.uploadThumbnail || uploadImg.path, e)
                setDraggingItemId(uploadImg.id)
                dragStart(e)
              }}
              onDragEnter={e => setDragoverItemId(uploadImg.id)}
              onDragLeave={e => setDragoverItemId('')}
              onDragOver={e => {
                setIsDragToAfter(isDropToAfter(e))
                dragOver(e)
              }}
              onDrop={dragDrop}
              onDragEnd={() => {
                setDraggingItemId('')
                setDragoverItemId('')
                removeDragGhostImageDom()
              }}
              onTouchStart={handleTouchStartWithVisual(uploadImg.id)}
              onTouchMove={handleTouchMoveWithVisual}
              onTouchEnd={handleTouchEndWithVisual}
              onTouchCancel={handleTouchEndWithVisual}
              onContextMenu={e => e.preventDefault()}
              id={uploadImg.id}
              customStyle={css({
                '.upload-place-picture': {
                  backgroundColor: uploadImg.id === draggingItemId ? 'rgba(205,205,205, 0.7) !important' : 'auto',
                },
              })}
              aspect={16 / 9}
            />
            {dragoverItemId === uploadImg.id && <div className={isDragToAfter ? 'draggable-line -right' : 'draggable-line -left'}></div>}
            <img
              className="input-remove-icon"
              src={require('@/static/images/deleat_icon.svg')}
              onClick={e => {
                e.stopPropagation()
                if (uploadImg.uploadThumbnail?.startsWith('blob:')) {
                  URL.revokeObjectURL(uploadImg.uploadThumbnail)
                }
                imageInput.remove(index)
              }}
            ></img>
          </div>
        ))}

        <AddImageButton
          feature="cleaning"
          onUpload={({ uploadedPath, file }) =>
            imageInput.append({
              path: uploadedPath,
              uploadThumbnail: URL.createObjectURL(file),
            })
          }
          multiple
        />
      </div>
      {control.getFieldState('images').error?.message && <div className="error-text">{control.getFieldState('images').error?.message}</div>}
    </div>
  )
}

const inputImageStyle = css({
  position: 'relative',
  '.image-tile': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    '[data-draggable-image-id]': {
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      WebkitTapHighlightColor: 'transparent',
      img: {
        WebkitUserDrag: 'none',
      },
    },
    '.input-remove-icon': {
      position: 'absolute',
      width: 19,
      cursor: 'pointer',
      top: '-5px',
      right: '-5px',
    },
    '.draggable-line': {
      '&.-left': {
        borderLeft: 'solid 2px #f2a40b',
        left: -9,
      },
      '&.-right': {
        borderRight: 'solid 2px #f2a40b',
        right: -9,
      },
      top: -6,
      position: 'absolute',
      height: 'calc(100% + 12px)',
    },
    '.add-image-button': {
      width: 142.2,
      height: 80,
      background: '#F5F5F5 0% 0% no-repeat padding-box',
      border: '1px dashed #CCCCCC',
      borderRadius: 5,
      position: 'relative',
      marginBottom: 8,
      '.add-upload-place': {
        width: 142.2,
        height: 80,
        position: 'absolute',
        borderRadius: 5,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        cursor: 'pointer',
        backgroundImage: `url(${require('@/static/images/plus_yellow.svg')})`,
      },
    },
  },
  '.annotation-text': {
    fontSize: 10,
    color: '#676767',
  },
  '.error-text': {
    fontSize: 10,
    color: 'rgba(255, 0, 0, 0.5)',
    position: 'absolute',
    bottom: 12,
  },
})
